import React from "react";
import { Parser, INode, ProcessNodeDefinitions } from "html-to-react";
import URI from "urijs";
import getUrl, { stripRelativePath } from "./getUrl";
import { Link } from "react-router-dom";
import transformDocUrl from "./transformDocUrl";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { HashLink } from "react-router-hash-link";
import { Typography } from "@material-ui/core";
import ITocItem from "../models/ITocItem";
import { addToTree, removeParents, removeTrunk } from "./treeBuilder";

var processNodeDefinitions = new ProcessNodeDefinitions(React);
const parser = new Parser();

const headerKeys = ["h1", "h2", "h3", "h4", "h5", "h6"];

enum Header {
  h1 = "h1",
  h2 = "h2",
  h3 = "h3",
  h4 = "h4",
  h5 = "h5",
  h6 = "h6",
}

function isLocal(url: string): boolean {
  const host = URI(window.location.href)
    .host()
    .toLowerCase();
  const linkHost = URI(url)
    .host()
    .toLowerCase();

  return !host || !linkHost || host === linkHost;
}

function createAlertProcessor(classArray: Array<string>, clsAppend: string) {
  return {
    shouldPreprocessNode: function(node: INode) {
      if (node.attribs) {
        const cls = node.attribs["class"];
        return cls && classArray.filter(c => cls.includes(c)).length > 0;
      }
      return false;
    },
    preprocessNode: function(node: INode) {
      node.attribs["class"] = `${node.attribs["class"]} ${clsAppend}`;
      return node;
    },
  };
}

function createPreprocessors(name: string, onToc: Function) {
  return [
    createAlertProcessor(["NOTE", "TIP"], "alert alert-info"),
    createAlertProcessor(["WARNING"], "alert alert-warning"),
    createAlertProcessor(["IMPORTANT", "CAUTION"], "alert alert-danger"),
    {
      shouldPreprocessNode: function(node: INode) {
        return node.name === "div" && node.attribs["class"] === "toc-hidden-ref";
      },
      preprocessNode: function(node: INode) {
        let toc = node.attribs["toc"];
        if (toc) {
          toc = URI(toc)
            .absoluteTo(`/${name}`)
            .toString();
          toc = `${toc.substring(1, toc.length - 5)}.json`;
          onToc(toc);
        }
        return node;
      },
    },
    {
      shouldPreprocessNode: function(node: INode) {
        return node.name === "a";
      },
      preprocessNode: function(node: INode) {
        let href = node.attribs["href"];
        if (!href) {
          href = "index.html";
        }
        if (isLocal(href)) {
          href = transformDocUrl(href, name);
        }
        node.attribs["href"] = href;
        return node;
      },
    },
    {
      shouldPreprocessNode: function(node: INode) {
        return node.name === "img";
      },
      preprocessNode: function(node: INode) {
        let href = node.attribs["src"];

        let newHref = URI(href)
          .absoluteTo(`/${name}`)
          .toString()
          .substring(1);
        newHref = URI(getUrl("/docfx"))
          .query({ name: newHref })
          .toString();
        node.attribs["src"] = newHref;
        return node;
      },
    },
  ];
}

const createProcessors = (onHeader?: Function) => {
  return [
    {
      shouldProcessNode: function(node: INode) {
        return node.name === "a";
      },
      processNode: function(node: INode, children: React.ReactNode) {
        let href = node.attribs["href"];
        if (isLocal(href)) {
          href = stripRelativePath(href);
        }
        if (isLocal(href) && href.includes("#")) {
          return (
            <HashLink smooth to={href}>
              {node.data || children}
            </HashLink>
          );
        } else {
          return isLocal(href) ? <Link to={href}>{node.data || children}</Link> : <a href={href}>{node.data || children}</a>;
        }
      },
    },
    {
      shouldProcessNode: function(node: INode) {
        return node.name === "code";
      },
      processNode: function(node: INode, children: React.ReactNode) {
        let cls = node.attribs["class"] as string;
        let lang = null;
        if (cls) {
          let classes = cls
            .split(" ")
            .map(s => s.trim())
            .filter(s => s.startsWith("lang-"));
          if (classes.length > 0) {
            lang = classes[0].substring(5);
          }
        }
        if (lang === "restApi") {
          // this looks better than HTTP or REST or bash
          lang = "javascript";
        }
        if (lang) {
          return (
            <SyntaxHighlighter language={lang} style={atomDark}>
              {node.data || children}
            </SyntaxHighlighter>
          );
        } else {
          // todo style like above someday if you feel like it
          return <code>{node.data || children}</code>;
        }
      },
    },
    {
      shouldProcessNode: function(node: INode) {
        return node.name && headerKeys.includes(node.name.toLowerCase());
      },
      processNode: function(node: INode, children: React.ReactNode) {
        const name = node.name.toLowerCase() as Header;
        const id = node.attribs["id"];
        node.attribs["id"] = undefined;
        if (onHeader) {
          onHeader({
            node,
            id,
            text: Array.isArray(children) && children.length ? children[0] : "",
          });
        }

        if (headerKeys.includes(node.name.toLowerCase())) {
          return (
            <Typography key={id} variant={name} id={id}>
              {children}
            </Typography>
          );
        }
      },
    },
    {
      // fallback to the default processor
      shouldProcessNode: function(node: INode) {
        return true;
      },
      processNode: processNodeDefinitions.processDefaultNode,
    },
  ];
};

const isValidNode = () => true;

export default function fromHtml(html: string, name: string): { node: React.ReactNode; toc?: string; tocTree?: ITocItem } {
  let toc = undefined;
  let tocTree: ITocItem = {
    name: "",
    href: "",
    level: 0,
    items: [],
    leaf: false,
  };
  let curTocParent: ITocItem = tocTree;
  const node = parser.parseWithInstructions(
    html,
    isValidNode,
    createProcessors((header: { node: INode; id: string; text: string }) => {
      let level = parseInt(header.node.name.substring(1), 10);
      const item: ITocItem = {
        name: header.text,
        href: `/docs${document.location.search}#${header.id}`,
        level: level,
        items: [],
        leaf: true,
      };
      curTocParent = addToTree(curTocParent, item);
    }),
    createPreprocessors(name, (t?: string) => {
      toc = t;
    }),
  );

  let newTocTree = removeParents(tocTree);
  newTocTree = removeTrunk(tocTree, 1);

  return {
    node,
    toc,
    tocTree: newTocTree.items.length ? newTocTree : undefined,
  };
}
